import apiClient from './apiClient';

export interface AnalyticsFilters {
  start_date?: string;
  end_date?: string;
  modality?: string;
  body_part?: string;
  cpt_code?: string;
  radiologist_id?: number;
}

export interface DailyVolume {
  date: string;
  count: number;
}

export interface ReportVolumeMetric {
  metric: 'report_volume';
  data: {
    total: number;
    finalized: number;
    drafts: number;
    finalization_rate: number;
    daily: DailyVolume[];
  };
  generated_at: string;
}

export interface AnatomyPrevalenceItem {
  body_part: string;
  modality: string;
  count: number;
}

export interface AnatomyPrevalenceMetric {
  metric: 'anatomy_prevalence';
  data: {
    total: number;
    by_anatomy: AnatomyPrevalenceItem[];
  };
  generated_at: string;
}

export interface CptPrevalenceItem {
  cpt_code: string;
  exam_name: string;
  count: number;
}

export interface CptPrevalenceMetric {
  metric: 'cpt_prevalence';
  data: {
    total: number;
    by_cpt: CptPrevalenceItem[];
  };
  generated_at: string;
}

export interface RadiologistProductivity {
  user_id: number;
  username: string;
  reports_created: number;
  transcriptions: number;
  transcription_minutes: number;
  last_report_at: string | null;
}

export interface ProductivityMetric {
  metric: 'productivity';
  data: {
    total_users: number;
    radiologists: RadiologistProductivity[];
  };
  generated_at: string;
}

export interface TurnaroundTimeMetric {
  metric: 'turnaround_time';
  data: {
    count: number;
    average_seconds: number;
    median_seconds: number;
    p95_seconds: number;
  };
  generated_at: string;
}

export interface CohortDemographicsItem {
  gender: string;
  count: number;
}

export interface CohortDemographicsMetric {
  metric: 'cohort_demographics';
  data: {
    total_patients: number;
    by_gender: CohortDemographicsItem[];
  };
  generated_at: string;
}

export type AnalyticsMetric =
  | ReportVolumeMetric
  | AnatomyPrevalenceMetric
  | CptPrevalenceMetric
  | ProductivityMetric
  | TurnaroundTimeMetric
  | CohortDemographicsMetric;

export interface DashboardSnapshot {
  filters: AnalyticsFilters & { organization_id?: number | null };
  metrics: AnalyticsMetric[];
  generated_at: string;
}

const getDashboardSnapshot = async (filters: AnalyticsFilters = {}): Promise<DashboardSnapshot> => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, String(value));
    }
  });
  const resp = await apiClient.get<DashboardSnapshot>(`/api/v1/analytics/dashboard?${params.toString()}`);
  return resp.data;
};

export default {
  getDashboardSnapshot,
};
